/**
 * The sprite atlas.
 *
 * Every sprite is rasterised ONCE at boot into a single canvas, in white. Per
 * particle per frame the renderer then does one `setTransform` and one
 * `drawImage` — no Path2D fill, no filter, no per-particle colour work.
 *
 * Colour is handled by pre-tinting: the atlas is laid out as
 * `tints x sprites`, one row per palette colour, each built once with a
 * `source-in` composite. Blitting a pre-tinted cell is free; a per-particle
 * `filter: hue-rotate()` is not, and canvas `filter` support is uneven in Safari.
 *
 * `repeat` is where the bytes are. A snowflake is stored as ONE arm and drawn
 * n times around the centre at rasterisation time. The six-arm set measured
 * 3,028 B -> 521 B gzip for ~40 bytes of code here.
 */

import type { Atlas, AtlasCell, SpriteDef } from './types.ts';

type AnyCanvas = HTMLCanvasElement | OffscreenCanvas;
type AnyCtx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

function makeCanvas(w: number, h: number): AnyCanvas {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

/**
 * @param defs    sprite definitions, in catalogue order
 * @param cellPx  cell edge in DEVICE px — the sprite's largest on-screen size
 * @param colors  CSS colours; one atlas row each
 */
export function buildAtlas(defs: SpriteDef[], cellPx: number, colors: string[]): Atlas {
  const tints = Math.max(1, colors.length);
  const cols = defs.length;
  const canvas = makeCanvas(cols * cellPx, tints * cellPx);
  const g = canvas.getContext('2d') as AnyCtx;

  // ── One row per tint, each drawn DIRECTLY in its colour ──────────────────
  //
  // Deliberately not a composite. The obvious implementation — draw the shapes
  // once in white, then copy each row and recolour it with `source-in` — is
  // wrong, and wrong in a way that produces a completely blank atlas with no
  // error: `source-in` is a WHOLE-CANVAS operation, not clipped to the
  // fillRect, so it removes every existing pixel outside the filled rectangle.
  // Each tint row therefore erases the row before it, and the last one erases
  // everything. Caught by a headless render, not by review.
  //
  // Drawing directly costs `sprites x tints` fills once at boot — single digits
  // in practice — and has no compositing semantics to get wrong.
  for (let t = 0; t < tints; t++) {
    g.fillStyle = colors[t] ?? '#fff';
    for (let i = 0; i < cols; i++) {
      g.save();
      g.translate(i * cellPx, t * cellPx);
      drawSprite(g, defs[i], cellPx);
      g.restore();
    }
  }

  const cells: AtlasCell[] = [];
  for (let t = 0; t < tints; t++) {
    for (let i = 0; i < cols; i++) {
      cells.push({ x: i * cellPx, y: t * cellPx, w: cellPx, h: cellPx });
    }
  }

  return { canvas, px: cellPx, cells, tints };
}

/**
 * An atlas holding ONE merchant-supplied image.
 *
 * ── Why it is not just another `SpriteDef` ─────────────────────────────────
 *
 * Every other sprite is a vector path rasterised in a palette colour, and the
 * whole tinting scheme exists to make colour free. A merchant's photograph or
 * logo has its own colours — that is the point of uploading it — so this atlas
 * is deliberately ONE cell and ONE tint. Recolouring a merchant's logo would be
 * a bug, not a feature, and `cellIndex` already collapses to 0 when there is a
 * single sprite and a single tint, so the renderer needs no special case.
 *
 * The image is drawn to fit the cell, centred, preserving aspect ratio — a
 * merchant uploads a 160×90 banner and gets a 160×90 banner, not a squashed
 * square.
 */
export function buildImageAtlas(
  img: CanvasImageSource,
  w: number,
  h: number,
  cellPx: number
): Atlas {
  const canvas = makeCanvas(cellPx, cellPx);
  const g = canvas.getContext('2d') as AnyCtx;
  const k = Math.min(cellPx / Math.max(1, w), cellPx / Math.max(1, h));
  const dw = Math.max(1, Math.round(w * k));
  const dh = Math.max(1, Math.round(h * k));
  g.imageSmoothingEnabled = true;
  g.drawImage(img, Math.round((cellPx - dw) / 2), Math.round((cellPx - dh) / 2), dw, dh);
  return { canvas, px: cellPx, cells: [{ x: 0, y: 0, w: cellPx, h: cellPx }], tints: 1 };
}

/** Index into `atlas.cells` for a given sprite and tint row. */
export function cellIndex(atlas: Atlas, sprite: number, tint: number, spriteCount: number): number {
  const t = atlas.tints > 0 ? tint % atlas.tints : 0;
  return t * spriteCount + (sprite % spriteCount);
}

function drawSprite(g: AnyCtx, s: SpriteDef, cellPx: number) {
  if (typeof s.d === 'object') {
    // A text glyph — emoji cost zero asset bytes and carry no licence at all,
    // because a platform font glyph is not an asset we ship. (We deliberately do
    // NOT ship Twemoji: ~10 kB per glyph as SVG, plus a CC-BY attribution
    // obligation that would have to be rendered somewhere.)
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = `${Math.floor(cellPx * 0.82)}px system-ui, "Apple Color Emoji", "Segoe UI Emoji", sans-serif`;
    g.fillText(s.d.text, cellPx / 2, cellPx / 2);
    return;
  }

  const path = new Path2D(s.d);
  const n = Math.max(1, s.repeat ?? 1);
  // Path data is authored in a 0..100 box.
  const scale = cellPx / 100;

  for (let k = 0; k < n; k++) {
    g.save();
    g.translate(cellPx / 2, cellPx / 2);
    if (n > 1) g.rotate((k * Math.PI * 2) / n);
    g.translate(-cellPx / 2, -cellPx / 2);
    g.scale(scale, scale);
    if (s.under) {
      g.save();
      g.globalAlpha = 0.28;
      g.fill(new Path2D(s.under), s.rule ?? 'nonzero');
      g.restore();
    }
    g.fill(path, s.rule ?? 'nonzero');
    g.restore();
  }
}
